import { PageTitle } from "components/PageMeta/PageTitle";
import { pageName } from "constants/pageNames";
import { ProductTopBanner } from "../components/ProductTopBanner";
import { useProductContext } from "../context";
import { useEffect, useState } from "react";
import { useGlobalContext } from "context/GlobalContext";
import { fetchDropDownsApi } from "API/apis/master/getDropDowns";
import { message } from "constants/responses/message";
import { Button, Typography } from "@mui/material";
import { transformOptionsData } from "../util/transformOptionsData";
import { FormControlWrapper } from "components/Wrappers/FormComponentsWrappers/FormControlWrapper";
import { DROPDOWNS_FIELD_NAMES } from "constants/dropdownsConstants";
import { TextFieldWrapper } from "components/Wrappers/FormComponentsWrappers/TextFieldWrapper";
import { MultiselectCheckBoxChip } from "components/Select/MultiselectCheckBoxChip";
import { SingleSelect } from "components/Select/SingleSelect";
import { updatePmcChecklistApi } from "API/apis/product/updatePmcChecklist";
import { STATUS_CODES } from "constants/responses/statusCodes";
import { useNavigate } from "react-router-dom";
import { routeLinks } from "constants/paths";
import { PAGE_NOTES } from "constants/PageNotes";
import { fetchProductPmcChecklistApi } from "API/apis/product/getPmcChecklist";
import { useParams } from "react-router-dom";

const initialFormData = {
  projectEndScope: "a",
  specificPurchaseRequired: "",
  specificRequirementTeakWood: "",
  siteMeasurementRequired: "",
  clientSendingProduct: "",
  dispatchPlanClarity: "",
  clientsBillingAddress: "",
  callingDispatchContainer: "",
  dispatchTerms: "",
  earlyDelivery: "",
  midProjectMilestones: [],
  thingsInClientScope: [],
  sampleFinishDevelopmentRequired: [],
  specificRequirementMetal: [],
  thingsInClientScopeNotes: "",
  sampleFinishDevelopmentNotes: "",
  specificPurchaseNotes: "",
  siteMeasurementNotes: "",
  advanceReceived: "",
};

export const ProductPMCHandover = () => {
  const { id } = useParams();
  const { currentProject } = useProductContext();
  const {
    setIsInitializing,
    setError,
    setIsLoading,
    setSuccess,
    setCopy,
    copy,
  } = useGlobalContext();
  const [elementOptions, setElementOptions] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const navigate = useNavigate();
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsInitializing(true);
        const ids = id.split(",");
        const { data: dropDownData, status: dropDownStatus } =
          await fetchDropDownsApi();
        if (dropDownStatus !== 200) {
          throw new Error(message.error.generalFailure);
        }
        if (copy) {
          const { data: prdPMCCheckListData } =
            await fetchProductPmcChecklistApi(Number(copy));
          setFormData(prdPMCCheckListData);
        } else {
          const { data: prdPMCCheckListData } =
            await fetchProductPmcChecklistApi(ids[0]);
          setFormData(prdPMCCheckListData);
        }

        let optionObject = transformOptionsData(dropDownData);
        setElementOptions(optionObject);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsInitializing(false);
      }
    };
    fetchData();
  }, []);

  const handleSelectSingle = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleSelectCheckboxMultiple = (event) => {
    const {
      target: { value },
    } = event;
    setFormData({
      ...formData,
      [event.target.name]: typeof value === "string" ? value.split(",") : value,
    });
  };

  const handleTextFieldChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const { data, status } = await updatePmcChecklistApi({
        ...formData,
        productLotProcessingId: id.split(","),
      });
      if (status !== STATUS_CODES.SUCCESS) {
        throw new Error(data.message);
      }
      setSuccess(data?.message);
      setCopy(undefined);
      setFormData({ ...initialFormData });
      setIsLoading(false);
      navigate(routeLinks.projectManagementProject + "/" + currentProject);
    } catch (error) {
      setIsLoading(false);
      setError(error.message);
    }
  };
  return (
    <>
      <PageTitle pageName={pageName.product.pmcHandover} />
      <Typography fontStyle="italic">Note: {PAGE_NOTES.pmcHandover}</Typography>

      <ProductTopBanner buttonType="back" />
      <form onSubmit={handleSubmit}>
        <SingleSelect
          handleSelectSingle={handleSelectSingle}
          label="Project End Scope"
          name="projectEndScope"
          options={
            elementOptions[DROPDOWNS_FIELD_NAMES.PROJECT_END_SCOPE] || []
          }
          selectedOption={formData?.projectEndScope || ""}
        />

        <MultiselectCheckBoxChip
          label="Mid project milestones"
          name="midProjectMilestones"
          handleSelectCheckboxMultiple={handleSelectCheckboxMultiple}
          options={
            elementOptions[DROPDOWNS_FIELD_NAMES.MID_PROJECT_MILESTONES] || []
          }
          selectedOptions={formData?.midProjectMilestones || []}
        />
        <MultiselectCheckBoxChip
          label="Things in client scope"
          name="thingsInClientScope"
          handleSelectCheckboxMultiple={handleSelectCheckboxMultiple}
          options={
            elementOptions[DROPDOWNS_FIELD_NAMES.THINGS_IN_CLIENT_SCOPE] || []
          }
          selectedOptions={formData?.thingsInClientScope || []}
        />
        <TextFieldWrapper
          id="thingsInClientScopeNotes"
          name="thingsInClientScopeNotes"
          label="Things in Client Scope notes"
          variant="outlined"
          value={formData?.thingsInClientScopeNotes || ""}
          fullWidth
          onChange={handleTextFieldChange}
          required
        />
        <MultiselectCheckBoxChip
          label="Sample / Finish development required"
          name="sampleFinishDevelopmentRequired"
          handleSelectCheckboxMultiple={handleSelectCheckboxMultiple}
          options={
            elementOptions[
              DROPDOWNS_FIELD_NAMES.SAMPLE_FINISH_DEVELOPMENT_REQUIRED
            ] || []
          }
          selectedOptions={formData?.sampleFinishDevelopmentRequired || []}
        />
        <FormControlWrapper fullWidth>
          <Typography fontStyle="italic">
            Please clear specifics and where to take client approval, BD
            approval, or no approval required
          </Typography>
          <TextFieldWrapper
            id="sampleFinishDevelopmentNotes"
            name="sampleFinishDevelopmentNotes"
            label="Sample / Finish development notes"
            variant="outlined"
            value={formData?.sampleFinishDevelopmentNotes || ""}
            fullWidth
            onChange={handleTextFieldChange}
            required
          />
        </FormControlWrapper>
        <SingleSelect
          handleSelectSingle={handleSelectSingle}
          label="Specific purchase required"
          name="specificPurchaseRequired"
          options={
            elementOptions[DROPDOWNS_FIELD_NAMES.SPECIFIC_PURCHASE_REQUIRED] ||
            []
          }
          selectedOption={formData?.specificPurchaseRequired}
        />

        <FormControlWrapper fullWidth>
          <Typography fontStyle="italic">
            Details regarding all specific purchase
          </Typography>
          <TextFieldWrapper
            id="specificPurchaseNotes"
            name="specificPurchaseNotes"
            label="Specific purchase notes"
            variant="outlined"
            value={formData?.specificPurchaseNotes || ""}
            fullWidth
            onChange={handleTextFieldChange}
            required
          />
        </FormControlWrapper>
        <FormControlWrapper fullWidth>
          <Typography fontStyle="italic">According to quotation</Typography>
          <MultiselectCheckBoxChip
            label="Specific Requirement - Metal"
            name="specificRequirementMetal"
            handleSelectCheckboxMultiple={handleSelectCheckboxMultiple}
            options={
              elementOptions[DROPDOWNS_FIELD_NAMES.SPECIFIC_REQUIREMENT_METAL]
            }
            selectedOptions={formData?.specificRequirementMetal || []}
          />
          <SingleSelect
            handleSelectSingle={handleSelectSingle}
            label="Specific Requirement - Teak Wood"
            name="specificRequirementTeakWood"
            options={
              elementOptions[
                DROPDOWNS_FIELD_NAMES.SPECIFIC_REQUIREMENT_TEAK_WOOD
              ] || []
            }
            selectedOption={formData?.specificRequirementTeakWood}
          />
          <SingleSelect
            handleSelectSingle={handleSelectSingle}
            label="Site measurement required"
            name="siteMeasurementRequired"
            options={
              elementOptions[DROPDOWNS_FIELD_NAMES.SITE_MEASUREMENT_REQUIRED] ||
              []
            }
            selectedOption={formData?.siteMeasurementRequired}
          />
          <TextFieldWrapper
            id="siteMeasurementNotes"
            name="siteMeasurementNotes"
            label="Site measurement notes"
            variant="outlined"
            value={formData?.siteMeasurementNotes || ""}
            fullWidth
            onChange={handleTextFieldChange}
            required
          />
        </FormControlWrapper>
        <SingleSelect
          handleSelectSingle={handleSelectSingle}
          label="Is Client sending any products to us?"
          name="clientSendingProduct"
          options={
            elementOptions[DROPDOWNS_FIELD_NAMES.CLIENT_SENDING_PRODUCTS] || []
          }
          selectedOption={formData?.clientSendingProduct}
        />

        <SingleSelect
          handleSelectSingle={handleSelectSingle}
          label="Dispatch plan clarity"
          name="dispatchPlanClarity"
          options={
            elementOptions[DROPDOWNS_FIELD_NAMES.DISPATCH_PLAN_CLARITY] || []
          }
          selectedOption={formData?.dispatchPlanClarity}
        />

        <SingleSelect
          handleSelectSingle={handleSelectSingle}
          label="Client's billing address"
          name="clientsBillingAddress"
          options={
            elementOptions[DROPDOWNS_FIELD_NAMES.CLIENTS_BILLING_ADDRESS] || []
          }
          selectedOption={formData?.clientsBillingAddress}
        />

        <SingleSelect
          handleSelectSingle={handleSelectSingle}
          label="Who is calling the dispatch container?"
          name="callingDispatchContainer"
          options={
            elementOptions[DROPDOWNS_FIELD_NAMES.CALLING_DISPATCH_CONTAINER] ||
            []
          }
          selectedOption={formData?.callingDispatchContainer}
        />

        <SingleSelect
          handleSelectSingle={handleSelectSingle}
          label="Dispatch terms"
          name="dispatchTerms"
          options={elementOptions[DROPDOWNS_FIELD_NAMES.DISPATCH_TERMS] || []}
          selectedOption={formData?.dispatchTerms}
        />

        <SingleSelect
          handleSelectSingle={handleSelectSingle}
          label="Early delivery"
          name="earlyDelivery"
          options={elementOptions[DROPDOWNS_FIELD_NAMES.EARLY_DELIVERY] || []}
          selectedOption={formData?.earlyDelivery || ""}
        />

        <TextFieldWrapper
          id="approvedQuotation"
          name="approvedQuotation"
          label="Approved Quotation"
          variant="outlined"
          value={formData?.approvedQuotation || ""}
          fullWidth
          onChange={handleTextFieldChange}
          required
        />

        <TextFieldWrapper
          id="po"
          name="po"
          label="PO"
          variant="outlined"
          value={formData?.po || ""}
          fullWidth
          onChange={handleTextFieldChange}
          required
        />

        <SingleSelect
          handleSelectSingle={handleSelectSingle}
          label="Advance Received"
          name="advanceReceived"
          options={elementOptions[DROPDOWNS_FIELD_NAMES.ADVANCE_RECEIVED] || []}
          selectedOption={formData?.advanceReceived || null}
        />

        <TextFieldWrapper
          id="advanceConfirmation"
          name="advanceConfirmation"
          label="Advance Confirmation"
          variant="outlined"
          value={formData?.advanceConfirmation || ""}
          fullWidth
          onChange={handleTextFieldChange}
          required
        />

        <TextFieldWrapper
          id="designDrawingsDesignScopeFromClient"
          name="designDrawingsDesignScopeFromClient"
          label="Design Drawings Design Scope From Client"
          variant="outlined"
          value={formData?.designDrawingsDesignScopeFromClient || ""}
          fullWidth
          onChange={handleTextFieldChange}
          required
        />

        <TextFieldWrapper
          id="productToPcdCodeMapping"
          name="productToPcdCodeMapping"
          label="Product To Pcd Code Mapping"
          variant="outlined"
          value={formData?.productToPcdCodeMapping || ""}
          fullWidth
          onChange={handleTextFieldChange}
          required
        />

        <TextFieldWrapper
          id="dispatchDateConfirmation"
          name="dispatchDateConfirmation"
          label="Dispatch Date Confirmation"
          variant="outlined"
          value={formData?.dispatchDateConfirmation || ""}
          fullWidth
          onChange={handleTextFieldChange}
          required
        />

        <TextFieldWrapper
          id="shortTimelineApprovals"
          name="shortTimelineApprovals"
          label="Short Timeline Approvals"
          variant="outlined"
          value={formData?.shortTimelineApprovals || ""}
          fullWidth
          onChange={handleTextFieldChange}
          required
        />

        <TextFieldWrapper
          id="pythaHanmacShopDrawingApprovalFromSk"
          name="pythaHanmacShopDrawingApprovalFromSk"
          label="Pytha/HANMAC Shop Drawing Approval From Sk"
          variant="outlined"
          value={formData?.pythaHanmacShopDrawingApprovalFromSk || ""}
          fullWidth
          onChange={handleTextFieldChange}
          required
        />

        <TextFieldWrapper
          id="anyOtherProjectNotes"
          name="anyOtherProjectNotes"
          label="Any Other Project Notes"
          variant="outlined"
          value={formData?.anyOtherProjectNotes || ""}
          fullWidth
          onChange={handleTextFieldChange}
          required
        />

        <Button variant="contained" type="submit">
          Save
        </Button>
      </form>
    </>
  );
};
